import { AnimatePresence, motion } from 'framer-motion';
import { Suspense } from 'react';
import { lazily } from 'react-lazily';
import { Outlet, ScrollRestoration } from 'react-router-dom';

import { useCurrentCompany, useCurrentUser, useFeatureFlag } from '@bonusly/who-am-i';

import { SeasonalEffects } from '../seasonal/seasonal-effects';
import { AdminRouteTracker } from '../seasonal/use-is-admin-route';

// eslint-disable-next-line local-rules/no-cross-module-imports
const { Welcome } = lazily(() => import('../../modules/onboarding/welcome/welcome'));
// eslint-disable-next-line local-rules/no-cross-module-imports
const { WelcomeV2 } = lazily(() => import('../../modules/onboarding/welcome-v2'));

const transition = {
	duration: 0.4,
	ease: [0.68, -0.3, 0.265, 1.25] as const,
};

export const RootLayout = () => {
	const company = useCurrentCompany();
	const user = useCurrentUser();
	const isBizyLedOnboardingEnabled = useFeatureFlag('plg-bizy-led-onboarding');

	const isSuperAdmin = user?.super_admin;
	const needsSignupCompletion = company?.sign_up?.state === 'company_info';
	// Bizy-led (V2) onboarding is gated behind `plg-bizy-led-onboarding`. Everyone
	// else keeps the legacy (V1) signup. Both gate on the same `company_info` state.
	const showWelcomeV2 = needsSignupCompletion && isBizyLedOnboardingEnabled && !isSuperAdmin;
	const showWelcomeV1 = needsSignupCompletion && !isBizyLedOnboardingEnabled && !isSuperAdmin;

	return (
		<>
			<ScrollRestoration />
			{/*
				Pushes the current pathname into a tiny store so `useIsAdminRoute`
				works anywhere — including design-system leaf components like
				`Avatar` that may be rendered outside a router in tests/sandboxes.
			*/}
			<AdminRouteTracker />
			{/* Seasonal background, petals, etc. */}
			<SeasonalEffects />
			<AnimatePresence mode="wait">
				{showWelcomeV2 ? (
					<motion.div key="welcome-v2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={transition}>
						<Suspense>
							<WelcomeV2 />
						</Suspense>
					</motion.div>
				) : showWelcomeV1 ? (
					<motion.div key="welcome" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={transition}>
						<Suspense>
							<Welcome />
						</Suspense>
					</motion.div>
				) : (
					<motion.div key="outlet" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={transition}>
						<Outlet />
					</motion.div>
				)}
			</AnimatePresence>
		</>
	);
};
